import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { filterEntityItems, type EntityItemConfig } from "@salestrak/ui";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import type { Omittable } from "@salestrak/ui";

const SEARCH_DEBOUNCE_MS = 300;

export type UseEntitySearchPoolOptions = Omittable<{
  baseItems: EntityItemConfig[];
  runSearch: (trimmed: string) => Promise<EntityItemConfig[]>;
  exclude?: string[];
  // Change this when base query data changes to drop stale search results.
  epoch?: unknown;
}>;

export interface UseEntitySearchPoolResult {
  items: EntityItemConfig[];
  onSearchChange: (query: string) => void;
}

export function useEntitySearchPool({
  baseItems,
  runSearch,
  exclude,
  epoch,
}: UseEntitySearchPoolOptions): UseEntitySearchPoolResult {
  const searchResultsRef = useRef(new Map<string, EntityItemConfig>());
  const searchFetchRef = useRef(0);
  const [searchVersion, setSearchVersion] = useState(0);

  // Ref mutation only — no setState, to avoid re-render loops with Apollo's cache re-emission.
  const epochRef = useRef(epoch);
  if (epochRef.current !== epoch) {
    epochRef.current = epoch;
    searchResultsRef.current = new Map();
  }

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery.trim(), SEARCH_DEBOUNCE_MS);

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  useEffect(() => {
    if (!debouncedQuery) {
      if (searchResultsRef.current.size > 0) {
        searchResultsRef.current = new Map();
        setSearchVersion((v) => v + 1);
      }
      return;
    }
    const fetchId = ++searchFetchRef.current;
    runSearchRef.current(debouncedQuery)
      .then((results) => {
        if (searchFetchRef.current !== fetchId) return;
        const next = new Map<string, EntityItemConfig>();
        for (const item of results) next.set(item.id, item);
        searchResultsRef.current = next;
        setSearchVersion((v) => v + 1);
      })
      .catch((err) => console.warn("[useEntitySearchPool] Search failed:", err));
  }, [debouncedQuery]);

  const onSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Key off contents, not reference, to avoid re-render loops on a fresh array with the same IDs.
  const excludeKey = exclude?.join(",") ?? "";
  const excludeSet = useMemo(
    () => (exclude?.length ? new Set(exclude) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- excludeKey stands for the exclude array contents
    [excludeKey],
  );

  const trimmedQuery = searchQuery.trim();

  const items = useMemo<EntityItemConfig[]>(() => {
    const seen = new Set<string>();
    const all: EntityItemConfig[] = [];
    const add = (list: Iterable<EntityItemConfig>) => {
      for (const item of list) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          all.push(item);
        }
      }
    };
    if (!trimmedQuery) {
      add(baseItems);
    } else {
      // Filter both base and server results by the current query so a stale
      // server batch (for an older query) is dropped instead of flashing rows.
      add(filterEntityItems(baseItems, trimmedQuery));
      add(filterEntityItems([...searchResultsRef.current.values()], trimmedQuery));
    }
    return excludeSet ? all.filter((item) => !excludeSet.has(item.id)) : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchVersion stands for the ref'd server results; excludeKey for excludeSet
  }, [baseItems, searchVersion, excludeKey, trimmedQuery]);

  return { items, onSearchChange };
}
